import { motion } from "framer-motion";
import { staggerContainer, fadeUp, scaleIn } from "../animations/variants";

const services = [
  {
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none">
        <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
        <ellipse
          cx="24"
          cy="24"
          rx="12"
          ry="8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray="4 3"
        />
        <rect
          x="14"
          y="22"
          width="20"
          height="4"
          rx="2"
          fill="currentColor"
          opacity="0.5"
        />
        <circle cx="24" cy="20" r="3" fill="currentColor" opacity="0.6" />
      </svg>
    ),
    title: "CT Scan",
    subtitle: "32-Slice Computed Tomography",
    desc: "GE Brivo 32 CT scanner — fastest scan with low radiation, delivering high-resolution cross-sectional images of the entire body.",
    features: [
      "Whole Body CT",
      "CT Angiography",
      "Brain & Skull CT",
      "Emergency Scans",
    ],
    color: "from-blue-500 to-primary-600",
    lightColor: "bg-primary-50",
    textColor: "text-primary-600",
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none">
        <rect
          x="4"
          y="14"
          width="40"
          height="20"
          rx="10"
          stroke="currentColor"
          strokeWidth="2"
        />
        <rect
          x="10"
          y="18"
          width="28"
          height="12"
          rx="6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <rect
          x="18"
          y="20"
          width="12"
          height="8"
          rx="2"
          fill="currentColor"
          opacity="0.3"
        />
        <circle cx="24" cy="38" r="3" stroke="currentColor" strokeWidth="2" />
        <line
          x1="24"
          y1="34"
          x2="24"
          y2="41"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    ),
    title: "MRI Scan",
    subtitle: "1.5 Tesla Hdxt",
    desc: "1.5 Tesla Hdxt MRI with advanced magnet technology for superior soft tissue imaging without any radiation exposure.",
    features: [
      "Brain & Spine MRI",
      "Joint Imaging",
      "Whole Body MRI",
      "MR Angiography",
    ],
    color: "from-violet-500 to-purple-600",
    lightColor: "bg-violet-50",
    textColor: "text-violet-600",
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none">
        <circle cx="24" cy="18" r="10" stroke="currentColor" strokeWidth="2" />
        <path
          d="M18 28 L12 40 M30 28 L36 40 M18 40 L36 40"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="24" cy="18" r="5" fill="currentColor" opacity="0.25" />
        <path
          d="M20 16 L22 20 L26 14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Neuro Imaging",
    subtitle: "Brain & Spine Advanced Imaging",
    desc: "Specialized brain and spinal cord imaging to detect strokes, tumors, MS, and other neurological conditions with high precision.",
    features: [
      "Brain Tumor Detection",
      "Stroke Protocol",
      "Spine Disorders",
      "Acute Emergencies",
    ],
    color: "from-emerald-500 to-teal-600",
    lightColor: "bg-emerald-50",
    textColor: "text-emerald-600",
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none">
        <rect
          x="8"
          y="10"
          width="32"
          height="28"
          rx="4"
          stroke="currentColor"
          strokeWidth="2"
        />
        <line
          x1="16"
          y1="20"
          x2="32"
          y2="20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="16"
          y1="26"
          x2="28"
          y2="26"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle
          cx="34"
          cy="34"
          r="8"
          fill="currentColor"
          className="text-current"
          opacity="0.15"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M31 34 L33 36 L37 32"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Abdominal & Pelvic",
    subtitle: "Comprehensive Body Imaging",
    desc: "Detailed imaging of liver, kidneys, pancreas, uterus, ovaries, and other abdominal organs for accurate diagnosis.",
    features: [
      "Liver & Pancreas",
      "Uterus & Ovaries",
      "Kidneys & Adrenals",
      "MR Urography",
    ],
    color: "from-amber-500 to-orange-500",
    lightColor: "bg-amber-50",
    textColor: "text-amber-600",
  },
  {
    icon: (
      <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none">
        <path
          d="M24 4 L28 16 L40 16 L30 24 L34 36 L24 28 L14 36 L18 24 L8 16 L20 16 Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Angiography",
    subtitle: "Vascular & MR Angiograms",
    desc: "Non-invasive MR angiography for carotid, cranial, renal, and peripheral vessels — precise vascular mapping without catheterization.",
    features: [
      "Carotid Angiography",
      "Renal Angiography",
      "Peripheral Vessels",
      "MR Fistulogram",
    ],
    color: "from-cyan-500 to-sky-600",
    lightColor: "bg-cyan-50",
    textColor: "text-cyan-600",
  },
];

export default function ServicesSection() {
  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="section-subtitle">
            What We Offer
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="section-title text-gray-900 mb-5"
          >
            Our <span className="gradient-text">Diagnostic Services</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-gray-500 max-w-2xl mx-auto text-lg"
          >
            Equipped with the latest technology and staffed by experienced
            radiologists — every scan is a step towards better health.
          </motion.p>
          <motion.p
            variants={fadeUp}
            className="mt-3 text-base font-semibold tracking-wide"
            style={{ color: "#e8523a" }}
          >
            Affordable Pricing with Premium Quality Service.
          </motion.p>
        </motion.div>

        {/* Service Cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service) => (
            <motion.div
              key={service.title}
              variants={scaleIn}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="group bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-xl hover:border-primary-100 transition-all duration-300 cursor-pointer"
            >
              {/* Icon */}
              <div
                className={`w-16 h-16 rounded-2xl ${service.lightColor} ${service.textColor} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
              >
                {service.icon}
              </div>

              {/* Title */}
              <h3 className="font-poppins font-bold text-xl text-gray-900 mb-1">
                {service.title}
              </h3>
              <p className={`text-sm font-medium ${service.textColor} mb-3`}>
                {service.subtitle}
              </p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                {service.desc}
              </p>

              {/* Feature pills */}
              <div className="flex flex-wrap gap-2">
                {service.features.map((f) => (
                  <span
                    key={f}
                    className={`text-xs px-3 py-1 rounded-full ${service.lightColor} ${service.textColor} font-medium`}
                  >
                    {f}
                  </span>
                ))}
              </div>

              {/* Hover gradient bar */}
              <div
                className={`mt-5 h-0.5 rounded-full bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
